import { FilterModel, IServerSideGetRowsParams, SortModelItem } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { isString, lowerCase, mapKeys, mapValues, snakeCase } from 'lodash';
import { MutableRefObject, useCallback, useMemo } from 'react';

import { RangeEndFilterKeySuffix, RangeStartFilterKeySuffix } from 'core/constants';
import { AgGridFilterOption, SortDirection } from 'core/enums';
import { AgGridDataRow, AgGridGetRowsParams, GetManyResponse } from 'core/types';
import {
  formatDateForServer,
  getDateRangeForRelativeFilter,
  isRelativeDateFilterOption,
  removeNullValuesNestedObject,
  trimFilterText,
  trimTextFilterModel,
} from 'core/utils';

interface IProps {
  gridRef: MutableRefObject<AgGridReact>;
  pageSize: number;
  getRows: (
    params: AgGridGetRowsParams,
    gridRef?: AgGridReact
  ) => Promise<GetManyResponse<AgGridDataRow>>;
  setTotals: (totals: Record<string, number>) => void;
}

export const useAgGridServerSideDatasource = ({
  gridRef,
  pageSize,
  getRows,
  setTotals,
}: IProps) => {
  const agGridFiltersToServerMapper = useCallback(
    (filterModel: FilterModel) =>
      mapAgGridFiltersToServer(filterModel, gridRef.current?.api.getGridOption('quickFilterText')),
    [gridRef]
  );

  const agGridSortToServerMapper = (sortModel: SortModelItem[]) => {
    const sortField = [];
    const sortDir = [];

    sortModel.forEach(({ colId, sort }: SortModelItem) => {
      sortField.push(snakeCase(colId));
      sortDir.push(sort as SortDirection);
    });

    return {
      sortField,
      sortDir,
    };
  };

  const getRowsHandler = useCallback(
    ({ request, success, fail }: IServerSideGetRowsParams) => {
      const filters = agGridFiltersToServerMapper(request.filterModel);

      const { sortField, sortDir } = agGridSortToServerMapper(request.sortModel);

      const page = Math.floor(request.startRow / pageSize) + 1;

      getRows({ filters, sortField, sortDir, page, pageSize }, gridRef.current)
        .then(({ records, count, totals }) => {
          setTotals(totals);

          success({
            rowData: records,
            rowCount: count,
          });
        })
        .catch(fail);
    },
    [agGridFiltersToServerMapper, pageSize, getRows, setTotals, gridRef]
  );

  const serverSideDatasource = useMemo(
    () => ({
      getRows: getRowsHandler,
    }),
    [getRowsHandler]
  );

  return {
    serverSideDatasource,
  };
};

// Every server table sends its filters through here, so text values are trimmed once for all of
// them, whatever set the model: typing, a restored session or a saved filter.
export const mapAgGridFiltersToServer = (filterModel: FilterModel, quickFilterText?: string) => {
  const trimmedFilterModel = trimTextFilterModel(filterModel);

  const mappedFilterConditions = mapFiltersConditions(trimmedFilterModel);

  const mappedFiltersValues = mapFilters(trimmedFilterModel);

  const query = trimFilterText(quickFilterText);

  if (query) {
    mappedFiltersValues.query = query;
  }

  return {
    ...mappedFiltersValues,
    ...mappedFilterConditions,
  };
};

const mapFilters = (filters: FilterModel): Record<string, string> => {
  const processedFilters = processFilters(filters);

  const mappedFiltersKeys = mapFiltersKeys(processedFilters);

  const mappedFiltersValues = mapFiltersValues(mappedFiltersKeys);

  return removeNullValuesNestedObject(mappedFiltersValues);
};

const processFilters = (filters: FilterModel): FilterModel => {
  let newFilters = {};

  Object.entries(filters).forEach(([key, value]) => {
    let currentFilter = {};

    const { filterType } = value;

    switch (filterType) {
      case 'number':
        currentFilter = processAgNumberColumnFilter(key, value);

        break;
      default:
        currentFilter = {
          [key]: value,
        };
    }

    newFilters = { ...newFilters, ...currentFilter };
  });

  return newFilters;
};

const mapFiltersKeys = (filters: FilterModel) =>
  mapKeys(filters, ({ filterType, type }, key) => {
    switch (filterType) {
      case 'date':
        return `${key}DateRange`;
      case 'number':
        return getNumberFilterKeyByType(type, key);
      case 'text':
        return getTextFilterKeyByType(type, key);
      default:
        return key;
    }
  });

const mapFiltersValues = (filters: FilterModel) =>
  mapValues(filters, ({ filterType, type, filter, values, dateFrom, dateTo }) => {
    switch (filterType) {
      case 'text':
        return agGridTextFilterToServerMapper(type, filter);
      case 'set':
        return agGridSetFilterToServerMapper(values);
      case 'date':
        return agGridDateFilterToServerMapper(type, dateFrom, dateTo);
      default:
        return filter;
    }
  });

const mapFiltersConditions = (filterModel: FilterModel) => {
  const hasConditions = Object.values(filterModel).some(
    (filter) => filter.conditions?.length > 0 && filter.operator
  );

  if (!hasConditions) {
    return {};
  }

  const conditionsResult = {};

  Object.entries(filterModel).forEach(([key, value]) => {
    if (value.conditions?.length > 0 && value.operator) {
      const operator = lowerCase(value.operator);

      const mappedConditions = value.conditions.map((condition) =>
        mapFilters({ [key]: condition })
      );

      if (mappedConditions.length > 0) {
        conditionsResult[`${key}_${operator}`] = mappedConditions;
      }
    }
  });

  return conditionsResult;
};

const processAgNumberColumnFilter = (key: string, value): FilterModel => {
  const { type, filter, filterTo } = value;

  if (AgGridFilterOption.IN_RANGE !== type) {
    return {
      [key]: value,
    };
  }

  return {
    [`${key}${RangeStartFilterKeySuffix}`]: {
      ...value,
      type: AgGridFilterOption.GREATER_THAN_OR_EQUAL,
      filter: filter,
    },
    [`${key}${RangeEndFilterKeySuffix}`]: {
      ...value,
      type: AgGridFilterOption.LESS_THAN_OR_EQUAL,
      filter: filterTo,
    },
  };
};

const getNumberFilterKeyByType = (type: string, key: string) => {
  if (key.includes(RangeStartFilterKeySuffix)) {
    key = key.replace(RangeStartFilterKeySuffix, '');
  }

  if (key.includes(RangeEndFilterKeySuffix)) {
    key = key.replace(RangeEndFilterKeySuffix, '');
  }

  switch (type) {
    case AgGridFilterOption.NOT_EQUAL:
      return `${key}Ne`;
    case AgGridFilterOption.LESS_THAN:
      return `${key}Lt`;
    case AgGridFilterOption.LESS_THAN_OR_EQUAL:
      return `${key}To`;
    case AgGridFilterOption.GREATER_THAN:
      return `${key}Gt`;
    case AgGridFilterOption.GREATER_THAN_OR_EQUAL:
      return `${key}From`;
    default:
      return key;
  }
};

const getTextFilterKeyByType = (type: string, key: string) => {
  switch (type) {
    case AgGridFilterOption.STARTS_WITH:
      return `${key}StartsWith`;
    case AgGridFilterOption.ENDS_WITH:
      return `${key}EndsWith`;
    case AgGridFilterOption.NOT_EQUAL:
      return `${key}Ne`;
    case AgGridFilterOption.NOT_CONTAINS:
      return `${key}NotContains`;
    default:
      return key;
  }
};

const agGridTextFilterToServerMapper = (type: string, filter: string) => {
  switch (type) {
    case AgGridFilterOption.EQUALS:
      return `"${filter}"`;
    case AgGridFilterOption.BLANK:
      return '(empty)';
    case AgGridFilterOption.NOT_BLANK:
      return '(non-empty)';
    default:
      return filter;
  }
};

const agGridSetFilterToServerMapper = (values: string[] | number[]) => {
  return values.map((value: string | number) => {
    if (isString(value)) {
      return value.replace(/ /g, '_');
    }

    return value;
  });
};

const formatDateRangeForServer = (dateFrom: Date | null, dateTo: Date | null) =>
  `${formatDateForServer(dateFrom)}|${formatDateForServer(dateTo)}`;

const agGridDateFilterToServerMapper = (
  type: AgGridFilterOption,
  dateFrom: Date | null,
  dateTo: Date | null
) => {
  if (isRelativeDateFilterOption(type)) {
    const { from, to } = getDateRangeForRelativeFilter(type);

    return formatDateRangeForServer(from, to);
  }

  if (!dateFrom && !dateTo) {
    return null;
  }

  switch (type) {
    case AgGridFilterOption.EQUALS:
      return formatDateRangeForServer(dateFrom, dateFrom);
    case AgGridFilterOption.LESS_THAN:
      return formatDateRangeForServer(null, dateFrom);
    case AgGridFilterOption.GREATER_THAN:
      return formatDateRangeForServer(dateFrom, null);
    default:
      return formatDateRangeForServer(dateFrom, dateTo);
  }
};
